import React, { useEffect, useState } from "react";
import { marketingApi } from "../marketingApi";
import { Section, EmptyState, Loading } from "../components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, Clock, FileText, Trash2 } from "lucide-react";

const STATUS_META = {
  draft:          { label: "Borrador",   cls: "bg-slate-100 text-slate-700",  icon: FileText },
  pending_review: { label: "Pendiente",  cls: "bg-amber-100 text-amber-800",  icon: Clock },
  approved:       { label: "Aprobado",   cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rejected:       { label: "Rechazado",  cls: "bg-rose-100 text-rose-800",    icon: XCircle },
};

const empty = { title: "", channel: "blog", scheduled_at: "", body: "", tags: [] };

export default function EditorialTab() {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => marketingApi.editorialList(filter === "all" ? null : filter)
    .then((d) => setEntries(d.entries || []))
    .catch(() => setEntries([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Título obligatorio"); return; }
    try {
      if (editing) await marketingApi.editorialPatch(editing.id, form);
      else await marketingApi.editorialCreate(form);
      toast.success("Guardado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const act = async (id, action) => {
    try { await marketingApi.editorialTransition(id, { action }); toast.success("Estado actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Transición no permitida"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Borrar esta entrada?")) return;
    try { await marketingApi.editorialDelete(id); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se puede borrar"); }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ title: e.title, channel: e.channel, scheduled_at: e.scheduled_at || "", body: e.body || "", tags: e.tags || [] }); setOpen(true); };

  if (!entries) return <Loading />;

  return (
    <div className="space-y-4" data-testid="editorial-tab">
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900 rounded-lg">
        <div className="text-sm text-amber-900 dark:text-amber-200">
          <b>La aprobación interna no publica contenido.</b> Este calendario es solo para
          coordinar borradores y aprobaciones del equipo. Ninguna acción publica en redes,
          blog ni envía emails.
        </div>
      </div>

      <Section
        title="Calendario editorial"
        subtitle="Flujo: Borrador → Pendiente de aprobación → Aprobado / Rechazado"
        right={(
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="editorial-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="pending_review">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="editorial-new" onClick={openCreate} className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90"><Plus size={14} className="mr-1" /> Nuevo</Button>
          </div>
        )}
      >
        {entries.length === 0 ? (
          <EmptyState title="No hay entradas" description="Crea el primer borrador para empezar a planificar contenido." />
        ) : (
          <div className="space-y-2">
            {entries.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.draft;
              const Icon = meta.icon;
              return (
                <div key={e.id} data-testid={`editorial-entry-${e.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white dark:bg-[#131B2A] border border-slate-200 dark:border-[#25334D] rounded-lg hover:border-[#026FCB] transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${meta.cls}`}><Icon size={10} /> {meta.label}</span>
                      <span className="text-xs text-slate-500 uppercase">{e.channel}</span>
                      {e.scheduled_at && <span className="text-[11px] text-slate-500">· {e.scheduled_at}</span>}
                    </div>
                    <div className="font-medium mt-1 truncate">{e.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">por {e.author_email || e.author_id} · {(e.updated_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {e.status === "draft" && <Button size="sm" variant="outline" onClick={() => act(e.id, "submit_review")}>Enviar a revisión</Button>}
                    {e.status === "pending_review" && <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(e.id, "approve")}>Aprobar</Button>
                      <Button size="sm" variant="outline" onClick={() => act(e.id, "reject")}>Rechazar</Button>
                    </>}
                    {(e.status === "approved" || e.status === "rejected") && <Button size="sm" variant="outline" onClick={() => act(e.id, "revert_to_draft")}>Volver a borrador</Button>}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} disabled={!["draft","pending_review"].includes(e.status)}>Editar</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(e.id)} disabled={!["draft","rejected"].includes(e.status)}><Trash2 size={13} /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Editar entrada" : "Nueva entrada"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Título</label>
              <Input data-testid="editorial-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Canal</label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="editorial-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blog">Blog</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Fecha programada</label>
                <Input type="date" value={(form.scheduled_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Contenido (borrador local)</label>
              <Textarea data-testid="editorial-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="editorial-save" onClick={save} className="bg-[#0A2A5E] hover:bg-[#0A2A5E]/90">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
